import { useEffect } from 'react';

/**
 * Custom hook for handling keyboard shortcuts
 * @param {Object} shortcuts - Object mapping key combinations to handlers
 * @param {boolean} enabled - Whether shortcuts are enabled
 */
export const useKeyboardShortcuts = (shortcuts, enabled = true) => {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event) => {
      // Don't trigger shortcuts when typing in inputs
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
      }

      const key = event.key.toLowerCase();
      const ctrl = event.ctrlKey || event.metaKey;
      const shift = event.shiftKey;
      const alt = event.altKey;

      // Create key combination string
      let combination = '';
      if (ctrl) combination += 'ctrl+';
      if (shift) combination += 'shift+';
      if (alt) combination += 'alt+';
      combination += key;

      // Check for matching shortcut
      const handler = shortcuts[combination];
      if (handler) {
        event.preventDefault();
        handler(event);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts, enabled]);
};

/**
 * Hook specifically for positioning shortcuts
 * @param {Object} handlers - Object with positioning handler functions
 * @param {boolean} enabled - Whether shortcuts are enabled
 */
export const usePositioningShortcuts = (handlers, enabled = true) => {
  const shortcuts = {
    // Grid and guides
    'ctrl+g': handlers.toggleGrid,
    'ctrl+shift+g': handlers.toggleSnapGuides,
    
    // Alignment shortcuts
    'ctrl+shift+l': handlers.alignLeft,
    'ctrl+shift+c': handlers.alignCenter,
    'ctrl+shift+r': handlers.alignRight,
    'ctrl+shift+t': handlers.alignTop,
    'ctrl+shift+m': handlers.alignMiddle,
    'ctrl+shift+b': handlers.alignBottom,
    
    // Distribution shortcuts
    'ctrl+shift+h': handlers.distributeHorizontally,
    'ctrl+shift+v': handlers.distributeVertically,
    
    // Selection shortcuts
    'ctrl+a': handlers.selectAll,
    'escape': handlers.clearSelection,
    
    // Delete shortcut
    'delete': handlers.deleteSelected,
    'backspace': handlers.deleteSelected,
    
    // Undo/Redo shortcuts
    'ctrl+z': handlers.undo,
    'ctrl+y': handlers.redo,
    'ctrl+shift+z': handlers.redo, // Alternative redo shortcut
    
    // Movement shortcuts (fine adjustment)
    'arrowleft': (e) => handlers.nudge && handlers.nudge(-1, 0, e.shiftKey ? 10 : 1),
    'arrowright': (e) => handlers.nudge && handlers.nudge(1, 0, e.shiftKey ? 10 : 1),
    'arrowup': (e) => handlers.nudge && handlers.nudge(0, -1, e.shiftKey ? 10 : 1),
    'arrowdown': (e) => handlers.nudge && handlers.nudge(0, 1, e.shiftKey ? 10 : 1),
  };

  useKeyboardShortcuts(shortcuts, enabled);
};

export default useKeyboardShortcuts;